import React from "react";
import { useFormikContext } from "formik";

import { LOADING_STATE } from "@typings/reduxThunkActions";

import { DrawerManager } from "@features/DrawerManager/DrawerManager.www";

import { useTelemetry } from "@hooks/useTelemetry";

import { FIELD, FormValues } from "../ServiceSelector.types";

import {
  ZipCodeState,
  ZipCodeAction,
  ZIP_CODE_ACTION,
} from "./ZipCodeField.types";
import { getSuggestions } from "./ZipCodeField.getSuggestions";

export const zipCodeDrawerId = "zipCodeDrawer";

export const suggestionsReducer = (
  state: ZipCodeState,
  action: ZipCodeAction,
): ZipCodeState => {
  switch (action.type) {
    case ZIP_CODE_ACTION.FETCH_SUGGESTIONS:
      return { ...state, status: LOADING_STATE.LOADING };
    case ZIP_CODE_ACTION.FAILED_SUGGESTIONS:
      return { ...state, status: LOADING_STATE.FAILED };
    case ZIP_CODE_ACTION.SET_SUGGESTIONS:
      return {
        status: LOADING_STATE.SUCCEEDED,
        suggestions: action.payload || [],
      };
    default:
      return state;
  }
};

export const useHelpers = (
  dispatch: React.Dispatch<ZipCodeAction>,
  state: ZipCodeState,
) => {
  const { values, setFieldValue, submitForm } = useFormikContext<FormValues>();
  const { sendEvent } = useTelemetry();
  const { showDrawer, hideDrawer, isDrawerActive } = DrawerManager.useManager();

  const scrollPosition = React.useRef(0);

  const changeHandler = async (query: string): Promise<void> => {
    setFieldValue(FIELD.ZIPCODE, query);
    setFieldValue(FIELD.COVERED, false);
    getSuggestions({
      serviceType: values[FIELD.SERVICE],
      query,
      dispatch,
    });
  };

  const drawerInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    changeHandler(event.currentTarget.value);
  };

  const keyDownHandler = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault();

      sendEvent("service-selector-zipcode-submit-by-enter");
      submitForm();

      if (isDrawerActive(zipCodeDrawerId)) {
        hideDrawer(zipCodeDrawerId);
      }
    }
  };

  const buttonClickHandler =
    (value: string, covered = true) =>
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();

      setFieldValue(FIELD.ZIPCODE, value, true);
      setFieldValue(FIELD.COVERED, covered, true);
      submitForm();

      if (isDrawerActive(zipCodeDrawerId)) {
        hideDrawer(zipCodeDrawerId);
      }
    };

  const focusHandler = (value: string) => {
    sendEvent("service-selector-zipcode-focus", {
      context: {
        value,
      },
    });
  };

  const blurHandler = (value: string) => {
    sendEvent("service-selector-zipcode-blur", {
      context: {
        value,
        suggestions: state.suggestions,
        suggestionsStatus: state.status,
      },
    });
  };

  const restoreScrollPosition = () => {
    setTimeout(() => {
      window.scrollTo(0, scrollPosition.current);
      scrollPosition.current = 0;
    }, 0);
  };

  const clickHandler = (): void => {
    showDrawer(zipCodeDrawerId);
    focusHandler(values[FIELD.ZIPCODE]);

    //Save scroll position
    scrollPosition.current = window.scrollY;
    window.scrollTo(0, 0);
  };

  const drawerCloseHandler = (): void => {
    blurHandler(values[FIELD.ZIPCODE]);
    restoreScrollPosition();
  };

  return {
    changeHandler,
    keyDownHandler,
    buttonClickHandler,
    focusHandler,
    blurHandler,
    clickHandler,
    drawerInputChangeHandler,
    drawerCloseHandler,
  };
};
